import { Breadcrumbs, Divider, Flex, Group, IconFont, Select, Text, Tooltip } from '@byecode/ui'
import { DataSourceAbstract } from '@lighthouse/core'
import { FieldIconTypeMap, ListItem4ByecodeUi } from '@lighthouse/shared'
import type { FC } from 'react'
import React, { useCallback, useMemo } from 'react'
import { Controller } from 'react-hook-form'
import { useToggle } from 'react-use'

import { InnerDrawer } from '../../InnerDrawer'
import { listCommonStyles } from './constants'
import * as SC from './styles'

type AllowViewSortValue = {
    canDragViewSort?: boolean
}

interface AllowViewSortConfiguratorProps {
    value: AllowViewSortValue
    dataSource: DataSourceAbstract
    onChange?: (v: AllowViewSortValue) => void
    onBack?: () => void
}

interface AllowViewSortSettingProps {
    title: string
    type: string
    value: AllowViewSortValue
    dataSource: DataSourceAbstract
    onChange?: (v: AllowViewSortValue) => void
}

const AllowViewSortConfigurator: React.FC<AllowViewSortConfiguratorProps> = ({ value, dataSource, onChange, onBack }) => {
    const { canDragViewSort } = value

    const sortOptions = useMemo(() => {
        return Object.values(dataSource.schema)
            .filter(item => item.type === 'number')
            .map(item => ({
                label: item.name,
                value: item.id,
                icon: FieldIconTypeMap[item.type]
            }))
    }, [dataSource.schema])

    const handleChange = useCallback(
        (params: Partial<AllowViewSortValue>) => {
            onChange?.({ ...value, ...params })
        },
        [onChange, value]
    )

    return (
        <InnerDrawer>
            <Breadcrumbs items={[{ icon: <IconFont type="ArrowLeft" />, label: '拖拽排序', onClick: onBack }]} />
            <Divider color="var(--color-gray-200)" />
            <Group
                mode="switch"
                label={canDragViewSort ? '已开启' : '已关闭'}
                defaultOpen={false}
                opened={canDragViewSort}
                onCollapseChange={v => handleChange({ canDragViewSort: v })}
            >
                <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                    <Text>排序字段</Text>
                    <Controller
                        name="viewSortConfig.sortId"
                        render={({ field }) => {
                            return (
                                <Select
                                    style={{ width: 180 }}
                                    placeholder="请选择字段"
                                    options={sortOptions}
                                    value={field.value}
                                    onChange={field.onChange}
                                    searchable
                                />
                            )
                        }}
                    />
                </ListItem4ByecodeUi>
                <SC.ImportTriggerWorkflowTipWrapper style={{ margin: 8 }}>
                    当某记录的排序字段为空，则会自动按照创建时间进行排序，详见
                    <a
                        href="https://byecodehelp.yuque.com/org-wiki-byecodehelp-zavfcl/oevich/eqvibghgqy94uf2s#XqtKb"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        帮助文档
                    </a>
                </SC.ImportTriggerWorkflowTipWrapper>
            </Group>
        </InnerDrawer>
    )
}

export const AllowViewSortSetting: FC<AllowViewSortSettingProps> = ({ title, type, value, dataSource, onChange }) => {
    const { canDragViewSort } = value
    const [open, toggle] = useToggle(false)

    return (
        <>
            <ListItem4ByecodeUi
                styles={listCommonStyles}
                justifyContent="space-between"
                enableHover
                alignItems="center"
                onClick={() => toggle(true)}
            >
                <Flex gap="12px" alignItems="center">
                    <SC.StyledIconWrapper active={canDragViewSort}>
                        <IconFont type={type} size={18} fill={canDragViewSort ? 'var(--color-main)' : 'var(--color-gray-400)'} />
                    </SC.StyledIconWrapper>
                    <Flex gap={4} alignItems="center">
                        <div>{title}</div>
                        <Tooltip title="开启后可通过拖拽改变记录的排序。注意，开启后「设置>排序数据」的功能将失效。" width={200}>
                            <IconFont type="Question" fill="var(--color-gray-400)" size={16} />
                        </Tooltip>
                    </Flex>
                </Flex>
                <Flex gap="4px" alignItems="center" style={{ color: 'var(--color-gray-400)' }}>
                    <div>{canDragViewSort ? '已开启' : '已关闭'}</div>
                    <IconFont type="ArrowRightSmall" />
                </Flex>
            </ListItem4ByecodeUi>
            {open && <AllowViewSortConfigurator value={value} dataSource={dataSource} onChange={onChange} onBack={() => toggle(false)} />}
        </>
    )
}
